import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { environment } from "@infra-env/environment";
import type {
  BannerAdminView,
  BannerAuditEntry,
  BannerCreatePayload,
  BannerImageUploadIntent,
  BannerPublishedView,
  BannerPublishHistoryEntry,
  BannerUpdatePayload,
  IBannersAdminPort,
} from "@core-interfaces/services/banners-admin";

@Injectable({ providedIn: "root" })
export class BannersAdminApiService implements IBannersAdminPort {
  private readonly basePath = `${environment.apiUrl}/banner/admin`;

  private readonly publicPath = `${environment.apiUrl}/banner`;

  constructor(private readonly http: HttpClient) {}

  async listPublished(): Promise<BannerPublishedView[]> {
    const body = await firstValueFrom(
      this.http.get<{ data?: BannerPublishedView[] }>(this.publicPath),
    );
    return Array.isArray(body?.data) ? body.data : [];
  }

  async list(): Promise<BannerAdminView[]> {
    const body = await firstValueFrom(
      this.http.get<{ data?: BannerAdminView[] }>(`${this.basePath}/list`),
    );
    return Array.isArray(body?.data) ? body.data ?? [] : [];
  }

  async getById(id: string): Promise<BannerAdminView> {
    return firstValueFrom(
      this.http.get<BannerAdminView>(`${this.basePath}/${id}`),
    );
  }

  async create(payload: BannerCreatePayload): Promise<BannerAdminView> {
    return firstValueFrom(
      this.http.post<BannerAdminView>(this.basePath, payload),
    );
  }

  async update(
    id: string,
    payload: BannerUpdatePayload,
  ): Promise<BannerAdminView> {
    return firstValueFrom(
      this.http.patch<BannerAdminView>(`${this.basePath}/${id}`, payload),
    );
  }

  async publish(id: string): Promise<BannerAdminView> {
    return firstValueFrom(
      this.http.patch<BannerAdminView>(`${this.basePath}/${id}/publish`, {}),
    );
  }

  async unpublish(id: string): Promise<BannerAdminView> {
    return firstValueFrom(
      this.http.patch<BannerAdminView>(`${this.basePath}/${id}/unpublish`, {}),
    );
  }

  async deactivate(id: string): Promise<BannerAdminView> {
    return firstValueFrom(
      this.http.patch<BannerAdminView>(`${this.basePath}/${id}/deactivate`, {}),
    );
  }

  async delete(id: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.basePath}/${id}`, { responseType: "text" }),
    );
  }

  async createImageUploadIntent(
    originalName: string,
    mimeType: string,
  ): Promise<BannerImageUploadIntent> {
    return firstValueFrom(
      this.http.post<BannerImageUploadIntent>(
        `${this.basePath}/image-upload-intent`,
        { originalName, mimeType },
      ),
    );
  }

  /** Sube una imagen directamente a S3 usando una URL pre-firmada y retorna el `objectKey`. */
  async uploadImage(file: File): Promise<string> {
    const intent = await this.createImageUploadIntent(file.name, file.type);
    const putResponse = await fetch(intent.putUrl, {
      method: "PUT",
      headers: intent.requiredHeaders,
      body: file,
    });
    if (!putResponse.ok) {
      throw new Error(
        `Error subiendo imagen a S3 (HTTP ${putResponse.status}).`,
      );
    }
    return intent.objectKey;
  }

  async getAuditTrail(id: string, limit = 50): Promise<BannerAuditEntry[]> {
    const body = await firstValueFrom(
      this.http.get<{ data?: BannerAuditEntry[] }>(
        `${this.basePath}/${id}/audit-trail?limit=${limit}`,
      ),
    );
    return Array.isArray(body?.data) ? body.data : [];
  }

  async getVersionHistory(id: string): Promise<BannerPublishHistoryEntry[]> {
    const body = await firstValueFrom(
      this.http.get<{ data?: BannerPublishHistoryEntry[] }>(
        `${this.basePath}/${id}/versions`,
      ),
    );
    return Array.isArray(body?.data) ? body.data : [];
  }
}
